import { useAppConstantsContext } from '@ltvco/refresh-lib/ctx';
import {
  Box,
  Button,
  Modal,
  Stack,
  Text,
  useTheme,
} from '@ltvco/refresh-lib/theme';
import { useScreenSize } from '@ltvco/refresh-lib/utils';
import { Close, Download, Gavel } from '@mui/icons-material';
import DOMPurify from 'dompurify';

import {
  OpinionContentHtml,
  StyledDownloadButton,
} from 'routes/Dashboard/CourtCases/v2/shared';
import { DisplayOpinion } from 'routes/Dashboard/CourtCases/v2/types';
import { processHtmlWithCitations } from 'routes/Dashboard/CourtCases/v2/utils';
import { ModalContent } from 'routes/Dashboard/PersonalityTraits/PreTest/PersonalityTraitsSurvey/SurveySteps/components';

import { OpinionSelect } from './OpinionSelect';

interface CourtOpinionModalProps {
  open: boolean;
  onClose: () => void;
  activeView: 'opinions' | 'similarCases';
  availableOpinions: DisplayOpinion[];
  selectedOpinionIndex: number;
  setSelectedOpinionIndex: (index: number) => void;
  handleDownloadClick: () => void;
  cluster_id: string | undefined;
}
export const CourtOpinionModal = ({
  open,
  onClose,
  activeView,
  availableOpinions,
  selectedOpinionIndex,
  setSelectedOpinionIndex,
  handleDownloadClick,
  cluster_id,
}: CourtOpinionModalProps) => {
  const { isMobile } = useScreenSize();
  const theme = useTheme();
  const constants = useAppConstantsContext();

  const handleViewClick = () => {
    if (cluster_id) {
      const opinionUrl = `${constants.links.baseUrl}/dashboard/court-cases/opinions/${cluster_id}`;
      window.open(opinionUrl, '_blank', 'noopener,noreferrer');
    }
    onClose();
  };
  return (
    <Modal open={open} onClose={onClose}>
      <ModalContent sx={{ backgroundColor: theme.palette.background.paper }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          mt={2.5}
          mb={2.5}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Gavel width={20} height={20} />
            <Text variant="h5" sx={{ fontWeight: 600 }} m={0}>
              Opinion
            </Text>
          </Box>
          <Button onClick={onClose} size="small" sx={{ padding: '4px' }}>
            <Close
              fontSize="small"
              sx={{ color: theme.palette.text.secondary }}
            />
          </Button>
        </Stack>
        <Box>
          {activeView === 'opinions' && availableOpinions.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Stack
                spacing={1}
                sx={{ mb: 2 }}
                flexDirection={isMobile ? 'column' : 'row'}
                justifyContent="space-between"
              >
                <OpinionSelect
                  opinions={availableOpinions}
                  selectedIndex={selectedOpinionIndex}
                  onSelect={setSelectedOpinionIndex}
                />
                <Box display="flex" gap={1}>
                  {availableOpinions[selectedOpinionIndex] &&
                    'download_url' in availableOpinions[selectedOpinionIndex] &&
                    availableOpinions[selectedOpinionIndex].download_url && (
                      <StyledDownloadButton
                        href={
                          availableOpinions[selectedOpinionIndex].download_url
                        }
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={handleDownloadClick}
                        sx={{
                          mb: 0.5,
                          borderColor: theme.palette.primary.main,
                          width: isMobile ? '100%' : 'auto',
                          justifyContent: isMobile
                            ? 'center !important'
                            : 'flex-start',
                        }}
                      >
                        <Download
                          fontSize="small"
                          sx={{ color: theme.palette.primary.main }}
                        />
                        <Text
                          variant="body2"
                          sx={{ color: theme.palette.primary.main }}
                        >
                          Download PDF
                        </Text>
                      </StyledDownloadButton>
                    )}
                </Box>
              </Stack>

              <Stack spacing={2}>
                <Box
                  sx={{
                    bgcolor: 'background.paper',
                    borderRadius: 1,
                  }}
                >
                  <OpinionContentHtml
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(
                        processHtmlWithCitations(
                          availableOpinions[selectedOpinionIndex]
                            ?.html_with_citations || ''
                        )
                      ),
                    }}
                  />
                </Box>
              </Stack>
              <Text
                variant="body1"
                sx={{ fontWeight: 400, mb: 2, mt: 2 }}
                color="text.secondary"
              >
                The text of these documents could have been obtained by
                analyzing a scanned document and may have typos.
              </Text>
            </Box>
          )}
        </Box>
        <Stack
          direction="row"
          justifyContent="end"
          alignItems="center"
          sx={{ mt: 2 }}
        >
          <Button
            onClick={onClose}
            size="small"
            sx={{ mt: 2, width: '81px', height: '42px' }}
          >
            <Text variant="body1" sx={{ color: theme.palette.primary.main }}>
              Cancel
            </Text>
          </Button>
          <Button
            onClick={handleViewClick}
            variant="contained"
            size="small"
            sx={{ mt: 2, width: '81px', height: '42px' }}
          >
            <Text variant="body1">View</Text>
          </Button>
        </Stack>
      </ModalContent>
    </Modal>
  );
};
